import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Input from '../Input/index';
import Loader from 'react-loader-spinner';
import { ReactComponent as IconSearch } from "@/assets/svgs/icon-search.svg";
import { ReactComponent as IconInfo } from "@/assets/svgs/Union-eye.svg";
import { ReactComponent as IconClear } from "@/assets/svgs/view-details-close.svg";

const LazyLoadableList = React.forwardRef(({ items, renderItems, loadTriggerRef, triggerIndex = 1, wrapperClass, listClass, itemClass, showSearch, testId, searchClass, searchPlaceholder, searchValue = '', onSearchCancel, onSearchChange, isLoading, emptyStateMessage }, ref) => {
    const searchInputClass = classNames(searchClass);

    const handleSearchChange = useCallback((e) => {
        e.preventDefault();
        if (e.target.value !== searchValue) {
            onSearchChange(e);
        }
    }, [searchValue, onSearchChange]);

    return (
        <div className={classNames(wrapperClass)} ref={ref}>
            {showSearch && (
                <div className={`${testId}__search`}>
                    <Input
                        prefix={<IconSearch />}
                        id={`${testId}-search`}
                        name="search"
                        type="text"
                        placeholder={searchPlaceholder || "Search"}
                        fullWidth
                        addClass={searchInputClass}
                        cancelIcon={<IconClear dataTestId={testId} />}
                        value={searchValue}
                        handleOnCancel={onSearchCancel}
                        onChange={handleSearchChange}
                    />
                </div>
            )}

            <div className={classNames(listClass)}>
                {items?.map((item, index) => (
                    <div
                        className={classNames(itemClass)}
                        data-testid={`${testId}__item__${item.id}`}
                        key={`${testId}__item__${index}`}
                        ref={index === items.length - triggerIndex ? loadTriggerRef : null}
                    >
                        {renderItems(item)}
                    </div>
                ))}

                {isLoading && (
                    <Loader
                        className={`${testId}__loader`}
                        data-testid={`${testId}__loader`}
                        type="ThreeDots"
                        color="grey"
                        height={20}
                        width={15}
                    />
                )}

                {!isLoading && items.length === 0 && (
                    <div className={`${testId}__empty-state`}>
                        <IconInfo />
                        <span>{emptyStateMessage}</span>
                    </div>
                )}
            </div>
        </div>
    );
});

LazyLoadableList.propTypes = {
    items: PropTypes.array.isRequired,
    renderItems: PropTypes.func.isRequired,
    loadTriggerRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.instanceOf(Element) })
    ]),
    triggerIndex: PropTypes.number,
    wrapperClass: PropTypes.string,
    listClass: PropTypes.string,
    itemClass: PropTypes.string,
    showSearch: PropTypes.bool,
    testId: PropTypes.string.isRequired,
    searchClass: PropTypes.string,
    searchPlaceholder: PropTypes.string,
    searchValue: PropTypes.string,
    onSearchCancel: PropTypes.func,
    onSearchChange: PropTypes.func,
    isLoading: PropTypes.bool,
    emptyStateMessage: PropTypes.string
};

LazyLoadableList.defaultProps = {
    triggerIndex: 1,
    searchValue: '',
};

export default React.memo(LazyLoadableList);